// app/javascript/dashboard/routes/dashboard/bulkmessage/utils/useFileValidation.js
import { ref } from 'vue';

export default function useFileValidation() {
  const validationErrors = ref([]);
  const validationWarnings = ref([]);

  // File size constants
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB maximum
  const LARGE_FILE_THRESHOLD = 3 * 1024 * 1024; // 3MB threshold for optimization
  const VERY_LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB threshold for warning

  // Supported file types
  const SUPPORTED_EXTENSIONS = ['csv', 'xlsx', 'xls'];
  const SUPPORTED_MIME_TYPES = [
    'text/csv',
    'application/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFileType = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidExtension = SUPPORTED_EXTENSIONS.includes(fileExtension);
    const isValidMimeType = SUPPORTED_MIME_TYPES.includes(file.type);

    return {
      isValid: isValidExtension || isValidMimeType,
      extension: fileExtension,
      mimeType: file.type
    };
  };

  const validateFileSize = (file) => {
    const errors = [];
    const warnings = [];
    const fileSize = file.size;
    const fileSizeMB = fileSize / (1024 * 1024);

    // Check maximum file size
    if (fileSize > MAX_FILE_SIZE) {
      errors.push({
        type: 'FILE_TOO_LARGE',
        message: `Arquivo muito grande (${formatFileSize(fileSize)}). O tamanho máximo permitido é ${formatFileSize(MAX_FILE_SIZE)}.`,
        size: fileSize,
        maxSize: MAX_FILE_SIZE
      });
    }

    // Check for very large files that might cause performance issues
    if (fileSize > VERY_LARGE_FILE_THRESHOLD && fileSize <= MAX_FILE_SIZE) {
      warnings.push({
        type: 'LARGE_FILE_WARNING',
        message: `Arquivo grande detectado (${formatFileSize(fileSize)}). O processamento pode levar mais tempo e consumir mais recursos.`,
        size: fileSize,
        threshold: VERY_LARGE_FILE_THRESHOLD,
        recommendations: [
          'O arquivo será processado usando otimizações especiais',
          'Recomendamos fechar outras abas do navegador durante o processamento',
          'Para melhor performance, considere dividir o arquivo em partes menores'
        ]
      });
    }

    // Check for files that will use optimized processing
    if (fileSize > LARGE_FILE_THRESHOLD && fileSize <= VERY_LARGE_FILE_THRESHOLD) {
      warnings.push({
        type: 'OPTIMIZED_PROCESSING',
        message: `Arquivo médio-grande detectado (${formatFileSize(fileSize)}). Será processado com otimizações automáticas.`,
        size: fileSize,
        threshold: LARGE_FILE_THRESHOLD,
        features: [
          'Processamento em lotes para melhor performance',
          'Indicador de progresso durante a importação', 
          'Validação otimizada para grandes volumes de dados'
        ]
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fileSize,
      fileSizeMB: parseFloat(fileSizeMB.toFixed(2)),
      shouldUseOptimized: fileSize > LARGE_FILE_THRESHOLD
    };
  };

  const validateFile = (file) => {
    validationErrors.value = [];
    validationWarnings.value = [];

    if (!file) {
      validationErrors.value.push({
        type: 'NO_FILE',
        message: 'Nenhum arquivo selecionado.'
      });
      return {
        isValid: false,
        errors: validationErrors.value,
        warnings: validationWarnings.value
      };
    }

    // Validate file type
    const typeValidation = validateFileType(file);
    if (!typeValidation.isValid) {
      validationErrors.value.push({
        type: 'INVALID_FILE_TYPE',
        message: `Tipo de arquivo não suportado. Use apenas arquivos CSV (.csv) ou Excel (.xlsx, .xls).`,
        fileType: typeValidation.extension,
        supportedTypes: SUPPORTED_EXTENSIONS
      });
    }

    // Validate file size
    const sizeValidation = validateFileSize(file);
    validationErrors.value.push(...sizeValidation.errors);
    validationWarnings.value.push(...sizeValidation.warnings);

    return {
      isValid: validationErrors.value.length === 0,
      errors: validationErrors.value,
      warnings: validationWarnings.value,
      shouldUseOptimized: sizeValidation.shouldUseOptimized,
      fileSize: sizeValidation.fileSize,
      fileSizeMB: sizeValidation.fileSizeMB,
      formattedSize: formatFileSize(file.size)
    };
  };

  const estimateProcessingTime = (fileSize, rowCount = null) => {
    // Rough estimates based on file size and complexity
    const fileSizeMB = fileSize / (1024 * 1024);
    
    let estimatedSeconds;
    
    if (rowCount) {
      // More accurate estimate if we know row count
      if (rowCount < 1000) {
        estimatedSeconds = 1;
      } else if (rowCount < 5000) {
        estimatedSeconds = 3;
      } else if (rowCount < 10000) {
        estimatedSeconds = 8;
      } else if (rowCount < 50000) {
        estimatedSeconds = 20;
      } else {
        estimatedSeconds = Math.min(60, rowCount / 2500); // Cap at 1 minute
      }
    } else {
      // Estimate based on file size
      if (fileSizeMB < 1) {
        estimatedSeconds = 1;
      } else if (fileSizeMB < 3) {
        estimatedSeconds = 2;
      } else if (fileSizeMB < 10) {
        estimatedSeconds = 5;
      } else if (fileSizeMB < 25) {
        estimatedSeconds = 15;
      } else {
        estimatedSeconds = Math.min(45, fileSizeMB * 2); // Cap at 45 seconds
      }
    }

    return {
      seconds: Math.round(estimatedSeconds),
      formatted: formatTime(estimatedSeconds)
    };
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${Math.round(seconds)} segundo${seconds !== 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      if (remainingSeconds === 0) {
        return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
      } else {
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} minutos`;
      }
    }
  };

  const getPerformanceRecommendations = (fileSizeMB) => {
    const recommendations = [];

    if (fileSizeMB > 25) {
      recommendations.push(
        'Feche outras abas e aplicações para liberar memória',
        'Evite usar o computador para outras tarefas durante o processamento',
        'Considere dividir o arquivo em partes menores para importações futuras'
      );
    } else if (fileSizeMB > 10) {
      recommendations.push(
        'Feche outras abas desnecessárias do navegador',
        'O processamento será feito em lotes para melhor performance'
      );
    } else if (fileSizeMB > 3) {
      recommendations.push(
        'O arquivo será processado com otimizações automáticas',
        'Um indicador de progresso será exibido durante a importação'
      );
    }

    return recommendations;
  };

  const clearValidation = () => {
    validationErrors.value = [];
    validationWarnings.value = [];
  };

  return {
    validationErrors,
    validationWarnings,
    validateFile,
    validateFileType,
    validateFileSize,
    estimateProcessingTime,
    formatFileSize,
    formatTime,
    getPerformanceRecommendations,
    clearValidation,
    // Constants
    MAX_FILE_SIZE,
    LARGE_FILE_THRESHOLD,
    VERY_LARGE_FILE_THRESHOLD,
    SUPPORTED_EXTENSIONS,
    SUPPORTED_MIME_TYPES
  };
}